import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import MomExport from "@/components/MomExport";
import ShareRecap from "@/components/ShareRecap";
import LiveTranscription from "@/components/LiveTranscription";
import { Button } from "@/components/ui/button";
import StatusBadge from "@/components/StatusBadge";
import { UrgencyBadge, ToneBadge } from "@/components/JevBadges";
import RecordingPanel from "@/components/RecordingPanel";
import MeetingRecap from "@/components/MeetingRecap";
import QualityReport from "@/components/QualityReport";
import TranscriptTimeline from "@/components/TranscriptTimeline";
import useLiveCaptions from "@/hooks/useLiveCaptions";
import { useAuth } from "@/contexts/AuthContext";
import { TEST_IDS } from "@/constants/testIds";
import { toast } from "sonner";
import { ArrowLeft, RefreshCw, Sparkles, ClipboardList, Gavel, AlertTriangle, HelpCircle, Radio, FileDown, Link as LinkIcon } from "lucide-react";

export default function MeetingDetail() {
  const { meetingId } = useParams();
  const { user } = useAuth();
  const [meeting, setMeeting] = useState(null);
  const [transcript, setTranscript] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [decisions, setDecisions] = useState([]);
  const [tab, setTab] = useState("overview");
  const [refreshing, setRefreshing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [retryingChunks, setRetryingChunks] = useState(false);

  async function retranscribe() {
    try {
      await api.post(`/meetings/${meetingId}/retranscribe`, {});
      toast.success("Re-transcription started — this runs in the background. Refresh in a minute.");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function retryFailedChunks() {
    setRetryingChunks(true);
    try {
      const { data } = await api.post(`/transcripts/${meetingId}/retry-failed-chunks`);
      if (data.recovered_chunks > 0) {
        toast.success(
          `Recovered ${data.recovered_chunks} chunk${data.recovered_chunks === 1 ? "" : "s"} · ${data.segments_added} new segments added` +
          (data.remaining_failed_chunks > 0 ? ` · ${data.remaining_failed_chunks} still failing` : "")
        );
      } else if (data.remaining_failed_chunks > 0) {
        toast.error(`Retry didn't recover any chunks — ${data.remaining_failed_chunks} still failing. Try again in a minute or use "Re-translate from audio".`);
      } else {
        toast.info(data.message || "Nothing to retry.");
      }
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRetryingChunks(false);
    }
  }

  // Auto-jump to Recap tab the first time we land on a Completed meeting with analytics.
  useEffect(() => {
    if (meeting?.status === "Completed" && meeting.analytics && tab === "overview") {
      // eslint-disable-next-line
      setTab("recap");
    }
    // eslint-disable-next-line
  }, [meeting?.status, meeting?.analytics]);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      // Meeting is essential; transcript/tasks/decisions are best-effort so a
      // single failing fetch never leaves the page stuck on "Loading…".
      const m = await api.get(`/meetings/${meetingId}`);
      setMeeting(m.data);
      const [t, ts, ds] = await Promise.allSettled([
        api.get(`/transcripts/${meetingId}`),
        api.get(`/tasks`, { params: { meeting_id: meetingId } }),
        api.get(`/decisions`, { params: { meeting_id: meetingId } }),
      ]);
      if (t.status === "fulfilled") setTranscript(t.value.data);
      if (ts.status === "fulfilled") setTasks(ts.value.data);
      if (ds.status === "fulfilled") setDecisions(ds.value.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRefreshing(false);
    }
  }, [meetingId]);

  // eslint-disable-next-line
  useEffect(() => { load(); }, [load]);

  // poll while processing
  useEffect(() => {
    if (meeting?.status === "Processing") {
      const id = setInterval(load, 4000);
      return () => clearInterval(id);
    }
    return undefined;
  }, [meeting?.status, load]);

  async function exportPdf() {
    setExporting(true);
    try {
      const res = await api.get(`/meetings/${meetingId}/minutes.pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const dispo = res.headers["content-disposition"] || "";
      const m = /filename="?([^"]+)"?/i.exec(dispo);
      a.download = m ? m[1] : `${meeting?.title || "minutes"}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Minutes PDF downloaded");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setExporting(false);
    }
  }

  async function regenerateSummary() {
    setRegenerating(true);
    try {
      const { data } = await api.post(`/ai/meetings/${meetingId}/regenerate-summary`);
      toast.success(`Summary refreshed · ${data.tasks_created} task${data.tasks_created === 1 ? "" : "s"}, ${data.decisions_created} decision${data.decisions_created === 1 ? "" : "s"}`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRegenerating(false);
    }
  }

  if (!meeting) {
    return <div className="p-8 text-sm text-muted-foreground">Loading…</div>;
  }

  const ai = meeting.ai_minutes;

  return (
    <div className="p-6 md:p-8 lg:p-12">
      <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
        <Link to="/meetings" data-testid="back-to-meetings-link" className="text-xs text-muted-foreground inline-flex items-center gap-1 underline underline-offset-4">
          <ArrowLeft className="w-3 h-3" /> Back to meetings
        </Link>
        <Button
          data-testid="refresh-meeting-btn"
          variant="outline"
          size="sm"
          onClick={load}
          disabled={refreshing}
          className="rounded-full h-9"
        >
          <RefreshCw className={`w-3 h-3 mr-2 ${refreshing ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      <header className="yl-surface yl-beam p-6 mb-8">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="eyebrow">
              {meeting.meeting_date} · {meeting.start_time}{meeting.end_time ? ` – ${meeting.end_time}` : ""}
            </div>
            <h1
              data-testid={TEST_IDS.meetingTitle}
              className="font-heading text-4xl tracking-tight mt-2"
            >
              {meeting.title}
            </h1>
            {meeting.description && (
              <p className="text-sm text-muted-foreground mt-3 max-w-2xl">{meeting.description}</p>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap min-w-0">
            <StatusBadge value={meeting.status} />
            {meeting.analytics?.meeting_tone && (
              <ToneBadge tone={meeting.analytics.meeting_tone} />
            )}
            <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground rounded-full bg-secondary px-2.5 py-1 font-semibold">
              {(meeting.participants || []).length} participants
            </div>
            {meeting.language && meeting.language !== "auto" && (
              <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground rounded-full bg-secondary px-2.5 py-1 font-semibold">
                Lang: {meeting.language}
              </div>
            )}
            <Button
              data-testid="copy-join-link-btn"
              type="button"
              size="sm"
              variant="outline"
              onClick={() => {
                const url = `${window.location.origin}/join/${meetingId}`;
                navigator.clipboard.writeText(url).then(
                  () => toast.success("Join link copied to clipboard"),
                  () => toast.error("Copy failed — link: " + url),
                );
              }}
              title="Anyone with this link can join with name + email."
              className="rounded-full h-8 px-3 text-[10px] uppercase tracking-[0.16em] font-semibold"
            >
              <LinkIcon className="w-3.5 h-3.5 mr-1.5" strokeWidth={2} />
              Copy join link
            </Button>
            <Button
              data-testid="regenerate-summary-btn"
              type="button"
              size="sm"
              variant="outline"
              onClick={regenerateSummary}
              disabled={regenerating}
              title="Re-run AI extraction on the latest transcript (use after editing speakers or re-translating)."
              className="rounded-full h-8 px-3 text-[10px] uppercase tracking-[0.16em] font-semibold"
            >
              {regenerating ? (
                <RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" strokeWidth={2} />
              ) : (
                <Sparkles className="w-3.5 h-3.5 mr-1.5" strokeWidth={2} />
              )}
              Regenerate summary
            </Button>
            <Button
              data-testid="export-pdf-btn"
              type="button"
              size="sm"
              variant="outline"
              onClick={exportPdf}
              disabled={exporting}
              className="rounded-full h-8 px-3 text-[10px] uppercase tracking-[0.16em] font-semibold"
            >
              {exporting ? (
                <RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" strokeWidth={2} />
              ) : (
                <FileDown className="w-3.5 h-3.5 mr-1.5" strokeWidth={2} />
              )}
              Export PDF
            </Button>
          </div>
        </div>

        {meeting.agenda?.length > 0 && (
          <div className="mt-5">
            <div className="eyebrow mb-2">Agenda</div>
            <ol className="list-decimal list-inside text-sm space-y-1">
              {(meeting.agenda || []).map((a, i) => <li key={i}>{a}</li>)}
            </ol>
          </div>
        )}
      </header>

      {meeting.transcription_coverage_ok === false && (
        <div
          data-testid="transcription-coverage-warning"
          className="rounded-2xl border border-amber-300 bg-amber-50 text-amber-900 px-5 py-4 flex items-start gap-3"
        >
          <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0" strokeWidth={2} />
          <div className="flex-1">
            <div className="font-semibold text-sm leading-snug">
              Transcription coverage warning
            </div>
            <p className="text-xs text-amber-800/90 mt-1 leading-relaxed">
              {meeting.transcription_failed_chunks} 10-minute chunk{meeting.transcription_failed_chunks === 1 ? "" : "s"} of this recording failed to transcribe after 3 retries. The rest of the meeting is captured.
            </p>
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <Button
                data-testid="retry-failed-chunks-btn"
                type="button"
                size="sm"
                onClick={retryFailedChunks}
                disabled={retryingChunks}
                className="rounded-full h-8 px-3 text-[10px] uppercase tracking-[0.16em] font-semibold text-white hover:opacity-90"
                style={{ background: "hsl(var(--ink-warning))" }}
              >
                {retryingChunks ? (
                  <RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" strokeWidth={2} />
                ) : (
                  <Sparkles className="w-3.5 h-3.5 mr-1.5" strokeWidth={2} />
                )}
                Retry failed chunks
              </Button>
              <span className="text-[11px] text-amber-800/70">
                Or click <strong>Re-translate from audio</strong> in the Transcript tab for a full re-run.
              </span>
            </div>
          </div>
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="rounded-full border border-border bg-card p-1 h-auto gap-1 shadow-card flex-wrap">
          <TabsTrigger data-testid={TEST_IDS.tabOverview} value="overview" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-1.5 text-[11px] uppercase tracking-[0.14em] font-semibold">Overview</TabsTrigger>
          {meeting.status === "Completed" && meeting.analytics && (
            <TabsTrigger data-testid="tab-recap" value="recap" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-1.5 text-[11px] uppercase tracking-[0.14em] font-semibold">Recap</TabsTrigger>
          )}
          <TabsTrigger data-testid={TEST_IDS.tabRecording} value="recording" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-1.5 text-[11px] uppercase tracking-[0.14em] font-semibold">Recording</TabsTrigger>
          <TabsTrigger data-testid={TEST_IDS.tabTranscript} value="transcript" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-1.5 text-[11px] uppercase tracking-[0.14em] font-semibold">Transcript</TabsTrigger>
          <TabsTrigger data-testid={TEST_IDS.tabAiMinutes} value="ai-minutes" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-1.5 text-[11px] uppercase tracking-[0.14em] font-semibold">AI Minutes</TabsTrigger>
          <TabsTrigger data-testid={TEST_IDS.tabTasks} value="tasks" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-1.5 text-[11px] uppercase tracking-[0.14em] font-semibold">Tasks</TabsTrigger>
          <TabsTrigger data-testid={TEST_IDS.tabDecisions} value="decisions" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-1.5 text-[11px] uppercase tracking-[0.14em] font-semibold">Decisions</TabsTrigger>
          <TabsTrigger data-testid="tab-mom" value="mom" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-1.5 text-[11px] uppercase tracking-[0.14em] font-semibold">Minutes doc</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-6 space-y-6">
          {transcript?.quality_report && (
            <QualityReport report={transcript.quality_report} onRetranscribe={retranscribe} />
          )}
          {meeting.transcript_quality?.jev_noise_ai && (
            <div
              data-testid="jev-noise-note"
              className="rounded-2xl border border-border bg-card px-5 py-3 text-xs text-muted-foreground flex items-center gap-2"
            >
              <Sparkles className="w-3.5 h-3.5 text-primary" strokeWidth={2} />
              <span>
                <strong className="text-foreground">Jev noise filter:</strong>{" "}
                {meeting.transcript_quality.jev_noise_flagged > 0
                  ? `${meeting.transcript_quality.jev_noise_flagged} of ${meeting.transcript_quality.jev_noise_checked} short segment${meeting.transcript_quality.jev_noise_checked === 1 ? "" : "s"} flagged as filler / noise`
                  : `no filler detected in ${meeting.transcript_quality.jev_noise_checked} short segment${meeting.transcript_quality.jev_noise_checked === 1 ? "" : "s"} checked`}
              </span>
            </div>
          )}
          {meeting.summary ? (
            <div className="border border-foreground p-6">
              <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-semibold mb-2">
                Meeting summary
              </div>
              <p className="text-sm leading-relaxed">{meeting.summary}</p>
            </div>
          ) : (
            <div className="border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
              Summary will appear once the meeting is recorded and processed.
            </div>
          )}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="border border-border p-5">
              <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-semibold mb-3">
                Participants
              </div>
              {(meeting.participants || []).length === 0 ? (
                <div className="text-sm text-muted-foreground">No participants added.</div>
              ) : (
                <ul className="space-y-1 text-sm">
                  {(meeting.participants || []).map((p, i) => (
                    <li key={i} className="flex items-center justify-between">
                      <span>{p.name}</span>
                      <span className="text-xs text-muted-foreground">{p.email || ""}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="border border-border p-5">
              <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-semibold mb-3">
                Meeting link
              </div>
              <code className="text-xs break-all">{meeting.meeting_link}</code>
            </div>
          </div>
        </TabsContent>

        {meeting.status === "Completed" && meeting.analytics && (
          <TabsContent value="recap" className="mt-6">
            <MeetingRecap
              meeting={meeting}
              tasksCount={tasks.length}
              decisionsCount={decisions.length}
            />
          </TabsContent>
        )}

        <TabsContent value="recording" className="mt-6">
          {meeting.status === "Processing" ? (
            <div className="yl-surface p-10 text-center">
              <div className="h-1.5 w-full bg-secondary rounded-full overflow-hidden mb-4">
                <div className="h-1.5 bg-primary rounded-full animate-pulse w-1/2" />
              </div>
              <div className="eyebrow">
                Processing — transcribing &amp; extracting intelligence
              </div>
              <div className="text-xs text-muted-foreground mt-2">
                This usually takes about 30 seconds for short meetings.
              </div>
            </div>
          ) : meeting.status === "Completed" && meeting.recording_id ? (
            <div className="yl-surface p-6">
              <div className="eyebrow mb-3">Recorded meeting</div>
              <audio
                controls
                className="w-full"
                src={`${process.env.REACT_APP_BACKEND_URL}/api/recordings/file/${meeting.recording_id}?auth=${encodeURIComponent(localStorage.getItem("meetiq_token") || "")}`}
              />
            </div>
          ) : meeting.status === "In Progress" && meeting.organizer_id !== user?.id ? (
            <PassiveLiveView meetingId={meeting.id} />
          ) : (
            <RecordingPanel meetingId={meeting.id} onUploaded={() => { setTab("overview"); load(); }} />
          )}
        </TabsContent>

        <TabsContent value="transcript" className="mt-6">
          <TranscriptTimeline
            transcript={transcript}
            meeting={meeting}
            onUpdated={load}
          />
        </TabsContent>

        <TabsContent value="ai-minutes" className="mt-6 space-y-6">
          {!ai ? (
            <div className="border border-dashed border-border p-10 text-center">
              <Sparkles className="w-6 h-6 mx-auto text-muted-foreground" />
              <div className="mt-3 text-sm text-muted-foreground">
                AI minutes will appear once a recording is processed.
              </div>
            </div>
          ) : (
            <>
              <Section title="Summary" icon={Sparkles}>
                <p className="text-sm leading-relaxed">{ai.meeting_summary}</p>
              </Section>
              <Section title="Decisions" icon={Gavel}>
                {ai.decisions?.length ? (
                  <ul className="space-y-3">
                    {(ai.decisions || []).map((d, i) => (
                      <li key={i} className="border border-border p-3">
                        <div className="font-medium">{d.decision}</div>
                        <div className="text-xs text-muted-foreground mt-1">
                          Owner: {d.owner_name || "Unassigned"} · Priority: {d.priority || "—"}
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : <Empty />}
              </Section>
              <Section title="Action items" icon={ClipboardList}>
                {ai.action_items?.length ? (
                  <ul className="space-y-3">
                    {(ai.action_items || []).map((a, i) => (
                      <li key={i} className="border border-border p-3">
                        <div className="font-medium flex items-center gap-2 flex-wrap">
                          {a.task_title}
                          <UrgencyBadge level={a.jev_urgency} />
                        </div>
                        <div className="text-sm text-muted-foreground mt-1">{a.task_description}</div>
                        <div className="text-xs text-muted-foreground mt-2 flex flex-wrap gap-3">
                          <span>👤 {a.assigned_to_name || "Unassigned"}</span>
                          <span>📅 {a.due_date || "No date"}</span>
                          <span>⚑ {a.priority}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : <Empty />}
              </Section>
              <Section title="Risks" icon={AlertTriangle}>
                {ai.risks?.length ? (
                  <ul className="space-y-3">
                    {(ai.risks || []).map((r, i) => (
                      <li key={i} className="border border-border p-3">
                        <div className="font-medium">{r.risk}</div>
                        <div className="text-xs text-muted-foreground mt-1">Impact: {r.impact}</div>
                        {r.suggested_mitigation && (
                          <div className="text-xs mt-1">Mitigation: {r.suggested_mitigation}</div>
                        )}
                      </li>
                    ))}
                  </ul>
                ) : <Empty />}
              </Section>
              <Section title="Open questions" icon={HelpCircle}>
                {ai.open_questions?.length ? (
                  <ul className="space-y-2">
                    {(ai.open_questions || []).map((q, i) => (
                      <li key={i} className="text-sm">
                        <span className="font-medium">{q.question}</span>
                        {q.raised_by && <span className="text-xs text-muted-foreground"> — {q.raised_by}</span>}
                      </li>
                    ))}
                  </ul>
                ) : <Empty />}
              </Section>
            </>
          )}
        </TabsContent>

        <TabsContent value="tasks" className="mt-6">
          {tasks.length === 0 ? (
            <div className="border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
              No tasks for this meeting yet.
            </div>
          ) : (
            <div className="border border-foreground divide-y divide-border">
              {tasks.map((t) => (
                <div key={t.id} className="p-4 flex items-start justify-between gap-4">
                  <div>
                    <div className="font-semibold">{t.title}</div>
                    <div className="text-xs text-muted-foreground mt-1">{t.description}</div>
                    <div className="text-[11px] text-muted-foreground mt-2 flex gap-3 flex-wrap">
                      <span>👤 {t.assigned_to_name || "Unassigned"}</span>
                      <span>📅 {t.due_date || "—"}</span>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <StatusBadge value={t.status} />
                    <StatusBadge value={t.priority} />
                    <UrgencyBadge level={t.jev_urgency} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="decisions" className="mt-6">
          {decisions.length === 0 ? (
            <div className="border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
              No decisions captured.
            </div>
          ) : (
            <div className="border border-foreground divide-y divide-border">
              {decisions.map((d) => (
                <div key={d.id} className="p-4 flex items-start justify-between gap-4">
                  <div>
                    <div className="font-medium">{d.decision_text}</div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Owner: {d.decision_owner_name || "—"}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <StatusBadge value={d.priority} />
                    <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground border border-border px-2 py-1">
                      {d.source}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
        <TabsContent value="mom" className="mt-6 space-y-6">
          <MomExport meetingId={meetingId} meetingTitle={meeting?.title} />
          <ShareRecap meetingId={meetingId} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <section className="border border-foreground p-5">
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4" strokeWidth={1.5} />
        <div className="text-[10px] uppercase tracking-[0.3em] font-bold">{title}</div>
      </div>
      {children}
    </section>
  );
}

function Empty() {
  return <div className="text-sm text-muted-foreground">None identified.</div>;
}

function PassiveLiveView({ meetingId }) {
  const { captions, alert } = useLiveCaptions(meetingId, true, 60);
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(t);
  }, []);
  return (
    <div data-testid="passive-live-view">
      <div className="yl-surface yl-beam px-5 py-3.5 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <Radio data-testid="live-indicator-icon" className="w-3.5 h-3.5 text-rose-500 yl-pulse-dot shrink-0" strokeWidth={2.4} />
          <span className="eyebrow text-[9px] truncate">Live meeting in progress</span>
        </div>
        <span className="font-mono text-[10px] text-muted-foreground shrink-0 uppercase tracking-wider">
          You are viewing, not recording
        </span>
      </div>
      <LiveTranscription captions={captions} agendaAlert={alert} live elapsed={elapsed} />
    </div>
  );
}
